<script setup lang="ts">
/** AR-E-ROND — Register of Nominee Directors (S.386AL). 이사 1인당 1부(상위 DocScope 스코프). */
import CddVarField from '../../aura/agm/cdd/CddVarField.vue';
import AgmSigners from '../../aura/agm/cdd/AgmSigners.vue';
import { useAgmFill } from '../../aura/agm/cdd/useCddFill';
import { DOC_SCOPE } from '../../aura/agm/cdd/useCddFill';
import { inject } from 'vue';
import NominatorTables from './NominatorTables.vue';
import '../../aura/agm/docs/agmDoc.css';

const fill = useAgmFill();
const scope = inject(DOC_SCOPE, '');
function val(k: string) { return String(fill.values[scope + k] ?? '').trim(); }
function setSel(k: string, v: string) { fill.setValue(scope + k, val(k) === v ? '' : v); }
</script>

<template>
  <!-- 1번(still not a nominee)·3번(still a nominee) 선택지의 "last declaration date" 는 별도 칸:
       last_declaration_date / last_declaration_date_still (템플릿 AR-E-ROND 도 같은 키). 셋 중 하나만 선택하는 항목이라 서로 따라 바뀌면 안 됨. -->
  <div class="agm-doc">
    <p class="sec-title" style="text-align:center">REGISTER OF NOMINEE DIRECTORS PURSUANT TO SECTION 386AL OF THE SINGAPORE COMPANIES ACT 1967</p>
    <table class="cdd-tbl" style="width:auto;margin-bottom:8px">
      <tbody>
        <tr><td class="cdd-lcell">To</td><td>:&nbsp; The Corporate Secretary</td></tr>
        <tr><td class="cdd-lcell">Name of Company</td><td>:&nbsp; <CddVarField field-key="company_name" label="Company Name" /></td></tr>
        <tr><td class="cdd-lcell">UEN</td><td>:&nbsp; <CddVarField field-key="company_uen" label="UEN" /></td></tr>
        <tr><td class="cdd-lcell">Name of Director</td><td>:&nbsp; <CddVarField field-key="director_name" label="Director name" /></td></tr>
      </tbody>
    </table>

    <p class="doc-p"><b>Definition of Nominee</b> — A director is a nominee if the director is accustomed or under an obligation (formal or informal) to act in accordance with the directions, instructions or wishes of any other person.</p>
    <p class="doc-p"><b>Please tick the appropriate box:</b></p>

    <p class="doc-p opt">
      <span class="doc-toggle" :class="{ off: val('nominee_dir_status') !== 'unchanged' }" @click="setSel('nominee_dir_status','unchanged')">{{ val('nominee_dir_status') === 'unchanged' ? '☒' : '☐' }}</span>
      I hereby confirm I’m <b>still not a nominee director</b> since my last declaration made on <CddVarField field-key="last_declaration_date" label="Last declaration date" />.
    </p>
    <p class="doc-p opt">
      <span class="doc-toggle" :class="{ off: val('nominee_dir_status') !== 'ceased' }" @click="setSel('nominee_dir_status','ceased')">{{ val('nominee_dir_status') === 'ceased' ? '☒' : '☐' }}</span>
      I hereby confirm I’ve <b>ceased to be a nominee director</b> with effect from <CddVarField field-key="cessation_date" label="Cessation date" />, and my nominator has also ceased on <CddVarField field-key="nominator_cessation_date" label="Nominator ceased date" />.
    </p>
    <p class="doc-p opt">
      <span class="doc-toggle" :class="{ off: val('nominee_dir_status') !== 'still' }" @click="setSel('nominee_dir_status','still')">{{ val('nominee_dir_status') === 'still' ? '☒' : '☐' }}</span>
      I hereby confirm that I’m <b>still a nominee director</b> and my nominator’s details <CddVarField field-key="nominator_update" label="remain the same / not the same" /> as provided in my last declaration made on <CddVarField field-key="last_declaration_date_still" label="Last declaration date (still a nominee)" /> / are to be updated as follows:
    </p>

    <NominatorTables />

    <div class="sign-block" style="margin-top:16px">
      <table class="cdd-tbl" style="width:auto">
        <tbody>
          <tr><td class="cdd-lcell">Name of director</td><td>:&nbsp; <CddVarField field-key="director_name" label="Director name" /></td></tr>
          <tr><td class="cdd-lcell">Date</td><td>:&nbsp; <CddVarField field-key="declaration_date" label="Date" /></td></tr>
        </tbody>
      </table>
      <div style="margin-top:8px"><AgmSigners var-key="rond_Signers" board-label="Director" /></div>
    </div>
  </div>
</template>

<style scoped>
/* 선택지 = 체크박스 + 한 문장. (flex 였을 때 글자·굵은글씨·날짜칸이 각각 좁은 세로 칸으로 쪼개졌다) */
.opt { display: block; padding-left: 22px; text-indent: -22px; line-height: 1.9; }
.opt > .doc-toggle { display: inline-block; width: 18px; text-indent: 0; margin-right: 4px; }
.opt :deep(*) { text-indent: 0; } /* 날짜·입력 칸(inline-block)이 음수 들여쓰기를 물려받지 않게 */
</style>
